import React, { useState, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";

const PRESET_DATES = {
  'last-24-hours': () => new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  'last-7-days':   () => new Date(Date.now() - 7  * 24 * 60 * 60 * 1000).toISOString(),
  'last-30-days':  () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  'last-90-days':  () => new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
};

const AuditFilters = ({ filters, onFilterChange, entityTypes = [], onApplyFilters }) => {
  const [customFromDate, setCustomFromDate] = useState(() =>
    filters.dateRange === 'custom' && filters.fromDate
      ? new Date(filters.fromDate).toISOString().split('T')[0]
      : ''
  );
  const [customToDate, setCustomToDate] = useState(() =>
    filters.dateRange === 'custom' && filters.toDate
      ? new Date(filters.toDate).toISOString().split('T')[0]
      : ''
  );
  const searchTimerRef = useRef(null);

  const eventTypeOptions = [
    { value: "all",         label: "All Actions" },
    { value: "Create",      label: "Create" },
    { value: "Update",      label: "Update" },
    { value: "Delete",      label: "Delete" },
    { value: "Distributed", label: "Distribution" },
    { value: "Sync",        label: "Sync" },
    { value: "Approve",     label: "Approved" },
  ];

  const dateRangeOptions = [
    { value: "last-24-hours", label: "Last 24 Hours" },
    { value: "last-7-days",   label: "Last 7 Days" },
    { value: "last-30-days",  label: "Last 30 Days" },
    { value: "last-90-days",  label: "Last 90 Days" },
    { value: "custom",        label: "Custom Range" },
  ];

  const entityTypeOptions = [
    { value: "all", label: "All Entities" },
    ...entityTypes.map(type => ({ value: type, label: type }))
  ];

  /** Apply updated filters immediately. */
  const apply = (newFilters) => {
    if (onApplyFilters) {
      onApplyFilters(newFilters);
    } else {
      onFilterChange(newFilters);
    }
  };

  const handleActionChange = (value) => apply({ ...filters, eventType: value });
  const handleEntityTypeChange = (value) => apply({ ...filters, entityType: value });

  const handleDateRangeChange = (value) => {
    const now = new Date();
    const presetFn = PRESET_DATES[value];
    const fromDate = presetFn ? presetFn() : (customFromDate ? new Date(customFromDate + 'T00:00:00').toISOString() : PRESET_DATES['last-7-days']());
    const toDate = value === 'custom' && customToDate ? new Date(customToDate + 'T23:59:59').toISOString() : now.toISOString();

    if (value === 'custom' && !customFromDate) {
      setCustomFromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    }
    if (value === 'custom' && !customToDate) {
      setCustomToDate(now.toISOString().split('T')[0]);
    }

    apply({ ...filters, dateRange: value, fromDate, toDate });
  };

  const handleCustomDateChange = (field, value) => {
    const now = new Date();
    if (field === 'from') {
      setCustomFromDate(value);
      if (value) {
        apply({ ...filters, dateRange: 'custom', fromDate: new Date(value + 'T00:00:00').toISOString(), toDate: filters.toDate || now.toISOString() });
      }
    } else {
      setCustomToDate(value);
      if (value) {
        apply({ ...filters, dateRange: 'custom', fromDate: filters.fromDate, toDate: new Date(value + 'T23:59:59').toISOString() });
      }
    }
  };

  /** Debounce search by 400 ms to avoid hammering the API on every keystroke. */
  const handleSearchChange = (value) => {
    clearTimeout(searchTimerRef.current);
    const snapshot = { ...filters, searchQuery: value };
    searchTimerRef.current = setTimeout(() => apply(snapshot), 400);
  };

  const handleReset = () => {
    setCustomFromDate('');
    setCustomToDate('');
    apply({
      eventType: 'all',
      dateRange: 'last-7-days',
      entityType: 'all',
      searchQuery: '',
      fromDate: PRESET_DATES['last-7-days'](),
      toDate: new Date().toISOString(),
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
     <h3 className="font-semibold flex items-center gap-2">
        <Icon name="Filter" size={18} />
        Filters
   </h3>
        <button
      onClick={handleReset}
      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
     Reset Filters
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
     {/* Search */}
     <div>
       <label className="block text-sm font-medium mb-1.5">Search</label>
   <div className="relative">
            <Icon
  name="Search"
   size={18}
         className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10"
         />
 <input
      type="text"
        placeholder="Search events..."
        defaultValue={filters?.searchQuery || ''}
          onChange={(e) => handleSearchChange(e.target.value)}
 className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>

        {/* Action Type */}
        <div>
          <label className="block text-sm font-medium mb-1.5">Action</label>
          <select
      value={filters?.eventType || 'all'}
      onChange={(e) => handleActionChange(e.target.value)}
    className="w-full h-10 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
      >
        {eventTypeOptions.map(option => (
        <option key={option.value} value={option.value}>
    {option.label}
              </option>
            ))}
       </select>
        </div>

        {/* Entity Type */}
        <div>
          <label className="block text-sm font-medium mb-1.5">Entity Type</label>
        <select
  value={filters?.entityType || 'all'}
      onChange={(e) => handleEntityTypeChange(e.target.value)}
        className="w-full h-10 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
            {entityTypeOptions.map(option => (
       <option key={option.value} value={option.value}>
        {option.label}
              </option>
        ))}
       </select>
    </div>

        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium mb-1.5">Date Range</label>
     <select
value={filters?.dateRange || 'last-7-days'}
            onChange={(e) => handleDateRangeChange(e.target.value)}
            className="w-full h-10 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
      {dateRangeOptions.map(option => (
      <option key={option.value} value={option.value}>
  {option.label}
      </option>
       ))}
          </select>
  </div>
      </div>

      {/* Custom Date Range */}
      {filters?.dateRange === 'custom' && (
  <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
    <label className="block text-sm font-medium mb-1.5">From Date</label>
        <Input
     type="date"
       value={customFromDate}
           onChange={(e) => handleCustomDateChange('from', e.target.value)}
      max={customToDate || new Date().toISOString().split('T')[0]}
        />
            </div>
            <div>
        <label className="block text-sm font-medium mb-1.5">To Date</label>
           <Input
       type="date"
    value={customToDate}
         onChange={(e) => handleCustomDateChange('to', e.target.value)}
         min={customFromDate}
        max={new Date().toISOString().split('T')[0]}
  />
     </div>
  </div>
   </div>
      )}

    </div>
  );
};

export default AuditFilters;
